import React from "react";
import CurrencyFormat from "react-currency-format";
import { getTruckImage, cleanTruckBodyLabel } from "./helpers";

/**
 * All-in price row + optional Book CTA. Used inline in the quote result and
 * in the desktop left-column pin when the inline block scrolls away.
 */
const QuotePriceSummary = ({
  truckType,
  sellingPrice,
  tripDistance = 0,
  tripDuration = 0,
  addOns = [],
  optionsStale = false,
  loading = false,
  onAccept,
  variant = "inline",
  showTerms = true,
  showBook = true,
  bookLabel = "Book",
  style,
}) => {
  const isPin = variant === "pin";
  const isMobileBar = variant === "mobile-bar";

  if (isMobileBar) {
    return (
      <div
        className={
          "lp2-price-mobile-bar" + (loading ? " is-refreshing" : "")
        }
        role="complementary"
        aria-label="Your quoted price"
      >
        <img
          className="lp2-price-mobile-bar-truck"
          src={getTruckImage(truckType)}
          alt=""
          loading="lazy"
          aria-hidden="true"
        />
        <div className="lp2-price-mobile-bar-meta">
          <span className="lp2-price-mobile-bar-label">
            {cleanTruckBodyLabel(truckType)} - all-in
          </span>
          <span className="lp2-price-mobile-bar-price">
            <CurrencyFormat
              value={sellingPrice / 100}
              displayType="text"
              thousandSeparator
              prefix="R"
              decimalScale={2}
            />
          </span>
          {tripDistance > 0 && (
            <span className="lp2-price-mobile-bar-trip">
              {Math.round(tripDistance / 1000)} km
            </span>
          )}
        </div>
        {showBook && (
          <button
            type="button"
            className="lp2-btn lp2-btn--primary lp2-price-mobile-bar-btn"
            onClick={onAccept}
            disabled={loading}
          >
            {bookLabel}
            <i className="mdi mdi-arrow-right" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        (isPin ? "lp2-price-pin" : "lp2-result-pin-block") +
        (loading ? " is-refreshing" : "")
      }
      style={style}
      role={isPin ? "complementary" : undefined}
      aria-label={isPin ? "Your quoted price" : undefined}
    >
      <div className="lp2-result-top">
        <img
          className="lp2-result-truck"
          src={getTruckImage(truckType)}
          alt={`${truckType} truck`}
          loading="lazy"
        />
        <div className="lp2-result-meta">
          <div className="lp2-result-label">
            {cleanTruckBodyLabel(truckType)}
            - all-in price
          </div>
          <div className="lp2-result-price">
            <CurrencyFormat
              value={sellingPrice / 100}
              displayType="text"
              thousandSeparator
              prefix="R"
              decimalScale={2}
            />
          </div>
          {tripDistance > 0 && (
            <div className="lp2-result-trip">
              <i className="mdi mdi-map-marker-distance" aria-hidden="true" />{" "}
              {Math.round(tripDistance / 1000)} km |{" "}
              {Math.round(tripDuration / 60)} min - driver &amp; fuel included
            </div>
          )}
          {addOns.length > 0 && (
            <ul
              className={
                "lp2-result-addons" + (optionsStale ? " is-stale" : "")
              }
              aria-label="Included add-ons"
            >
              {addOns.map((a) => (
                <li key={a.label} className="lp2-result-addon">
                  <i className={"mdi " + a.icon} aria-hidden="true" />
                  {a.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showBook && (
        <div className={"lp2-result-actions" + (isPin ? " lp2-result-actions--pin" : "")}>
          <button
            type="button"
            className="lp2-btn lp2-btn--primary lp2-btn--block lp2-btn--lg"
            onClick={onAccept}
            disabled={loading}
          >
            Book this vehicle
            <i className="mdi mdi-arrow-right" aria-hidden="true" />
          </button>
          {showTerms && !isPin && (
            <>
              <br />
              <br />
              <p className="lp2-result-terms">
                By continuing you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms
                </a>
                ,{" "}
                <a
                  href="/cancellation-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cancellation Policy
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuotePriceSummary;
